import { useEffect, useRef, useState } from 'react'

const W = 320

// ---- mini mel-spectrogram -----------------------------------------------
const SCOLS = 16
const SROWS = 8
const SPEC_H = 90

const STOPS = [
  [0.03, 0.02, 0.12],
  [0.34, 0.06, 0.45],
  [0.82, 0.18, 0.34],
  [1.0, 0.6, 0.15],
  [1.0, 0.96, 0.78],
]
function magma(e) {
  e = Math.max(0, Math.min(1, e)) * (STOPS.length - 1)
  const i = Math.min(STOPS.length - 2, Math.floor(e))
  const f = e - i
  const r = STOPS[i][0] + (STOPS[i + 1][0] - STOPS[i][0]) * f
  const g = STOPS[i][1] + (STOPS[i + 1][1] - STOPS[i][1]) * f
  const b = STOPS[i][2] + (STOPS[i + 1][2] - STOPS[i][2]) * f
  return `rgb(${(r * 255) | 0},${(g * 255) | 0},${(b * 255) | 0})`
}
function specEnergy(x, f, t) {
  const f0 = 0.12 + 0.05 * Math.sin(x * 7 + t)
  const gate = Math.max(0, Math.min(1, 0.5 + 0.6 * Math.sin(x * 11 + t)))
  let h = 0
  for (let k = 1; k <= 5; k++) h += (1 / k) * Math.exp(-((f - k * f0) ** 2) / 0.004)
  const env = Math.exp(-((f - 0.15) ** 2) / 0.02) + 0.6 * Math.exp(-((f - 0.45) ** 2) / 0.05)
  return h * env * gate * 1.1
}

// ---- transformer attention nodes ------------------------------------------
const NODE_COUNT = 6
const NODES = Array.from({ length: NODE_COUNT }, (_, i) => 20 + (i / (NODE_COUNT - 1)) * (W - 40))
const PAIRS = []
for (let i = 0; i < NODE_COUNT; i++) for (let j = i + 1; j < NODE_COUNT; j++) PAIRS.push([i, j])

// ---- transcript ------------------------------------------------------------
const RAW = ['wreck', 'a', 'nice', 'beach']
const FIXED = ['recognize', 'speech']
const CYCLE = 7

// Speech recognition as a top-to-bottom pipeline: mel-spectrogram sliced into
// overlapping chunks, a transformer reading them with self-attention, then
// tokens decoded to words with a correction pass — same stages as desktop.
export function Scene06Speech2D() {
  const cellRefs = useRef([])
  const sweepRef = useRef()
  const lineRefs = useRef([])
  const [phase, setPhase] = useState(0)
  const raf = useRef(0)

  useEffect(() => {
    let start = performance.now()
    const loop = (now) => {
      const t = (now - start) / 1000

      for (let col = 0; col < SCOLS; col++) {
        const x = col / (SCOLS - 1) - t * 0.08
        for (let row = 0; row < SROWS; row++) {
          const f = 1 - row / (SROWS - 1)
          const el = cellRefs.current[col * SROWS + row]
          if (el) el.setAttribute('fill', magma(specEnergy(x, f, t)))
        }
      }
      if (sweepRef.current) {
        const span = W - 60
        sweepRef.current.setAttribute('x', (20 + ((t * 0.6) % 1) * span - 20).toFixed(1))
      }

      const query = Math.floor((t * 1.4) % NODE_COUNT)
      PAIRS.forEach(([i, j], k) => {
        const active = i === query || j === query
        const el = lineRefs.current[k]
        if (el) el.setAttribute('opacity', active ? '0.8' : '0.08')
      })

      setPhase(t % CYCLE)
      raf.current = requestAnimationFrame(loop)
    }
    raf.current = requestAnimationFrame(loop)
    return () => cancelAnimationFrame(raf.current)
  }, [])

  const corrected = phase > 4.0
  const cellW = W / SCOLS
  const cellH = SPEC_H / SROWS
  const nodeY = 40

  return (
    <svg viewBox={`0 0 ${W} 480`} style={{ width: '100%', maxWidth: 340, height: 'auto', display: 'block', margin: '0 auto' }}>
      {/* mel-spectrogram sliced into overlapping chunks */}
      {Array.from({ length: SCOLS }).map((_, col) =>
        Array.from({ length: SROWS }).map((_, row) => (
          <rect
            key={`${col}-${row}`}
            ref={(el) => { cellRefs.current[col * SROWS + row] = el }}
            x={col * cellW} y={row * cellH}
            width={cellW + 0.5} height={cellH + 0.5}
            fill="#050414"
          />
        ))
      )}
      <rect ref={sweepRef} x="0" y="0" width="40" height={SPEC_H} fill="none" stroke="#7fe8ff" strokeWidth="2" opacity="0.95" />
      <text x={W / 2} y={SPEC_H + 20} fill="#5d7891" fontSize="10" fontFamily="monospace" textAnchor="middle" letterSpacing="0.06em">audio → overlapping chunks</text>

      <text x={W / 2} y={SPEC_H + 52} fill="#5577aa" fontSize="18" textAnchor="middle">↓</text>

      {/* transformer · self-attention */}
      <g transform={`translate(0, ${SPEC_H + 70})`}>
        {PAIRS.map(([i, j], k) => (
          <line
            key={k}
            ref={(el) => { lineRefs.current[k] = el }}
            x1={NODES[i]} y1={nodeY} x2={NODES[j]} y2={nodeY}
            stroke="#39d8ff" strokeWidth="1.5" opacity="0.1"
          />
        ))}
        {NODES.map((x, i) => (
          <circle key={i} cx={x} cy={nodeY} r="7" fill="#9fe9ff" />
        ))}
        <text x={W / 2} y={nodeY + 34} fill="#5d7891" fontSize="10" fontFamily="monospace" textAnchor="middle" letterSpacing="0.06em">transformer · self-attention</text>
      </g>

      <text x={W / 2} y={SPEC_H + 190} fill="#5577aa" fontSize="18" textAnchor="middle">↓</text>

      {/* tokens → words → corrected */}
      <g transform={`translate(0, ${SPEC_H + 230})`}>
        {!corrected && RAW.map((w, k) => (
          <text key={w} x={20} y={20 + k * 32} fill="#ffb24a" fontSize="18" fontFamily="system-ui, sans-serif"
            opacity={phase > 0.4 + k * 0.55 ? 1 : 0}>
            {w}
          </text>
        ))}
        {corrected && FIXED.map((w, k) => (
          <text key={w} x={20} y={20 + k * 32} fill="#46e89a" fontSize="20" fontFamily="system-ui, sans-serif">
            {w}
          </text>
        ))}
        <text x={W / 2} y={20 + RAW.length * 32 + 30} fill="#5d7891" fontSize="10" fontFamily="monospace" textAnchor="middle" letterSpacing="0.06em">tokens → words → corrected</text>
      </g>
    </svg>
  )
}
